import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { Award, Clock, ChevronDown, ChevronUp, Calendar, CheckCircle, Star, Users, ThumbsUp, AlertCircle, Sliders, CheckCircle2, Trophy, Crown, FileText, MessageSquare } from 'lucide-react';
import { vendorService } from '../../services/vendorService';
import { questionnaireService } from '../../services/questionnaireService';
import { projectService } from '../../services/projectService';

const VendorAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('performance');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [expandedVendor, setExpandedVendor] = useState(null);

  const [dateRange, setDateRange] = useState('30d');
  const [compareWeights, setCompareWeights] = useState(null);
  const [showCompareCalculations, setShowCompareCalculations] = useState(false);
  const [isWeightingExpanded, setIsWeightingExpanded] = useState(true);
  const [isComparisonExpanded, setIsComparisonExpanded] = useState(true);

  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load data from Supabase
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const [vendorsData, projectsData] = await Promise.all([
          vendorService?.getAll(),
          projectService?.getAll()
        ]);

        if (isMounted) {
          setVendors(vendorsData);
          setProjects(projectsData);

          // Load questionnaires for projects
          const allQuestionnaires = [];
          for (const project of projectsData) {
            const projectQuestionnaires = await questionnaireService?.getByProjectId(project?.id);
            allQuestionnaires?.push(...projectQuestionnaires);
          }
          setQuestionnaires(allQuestionnaires);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Mock questionnaire response data
  const responseData = {
    responseRates: [
      { month: 'Jul', submitted: 12, completed: 10, avgTime: 18 },
      { month: 'Aug', submitted: 18, completed: 16, avgTime: 15 },
      { month: 'Sep', submitted: 15, completed: 14, avgTime: 16 },
      { month: 'Oct', submitted: 22, completed: 20, avgTime: 14 },
      { month: 'Nov', submitted: 28, completed: 26, avgTime: 12 }
    ],
    categoryDistribution: [
      { name: 'Kitchen Remodeling', responses: 35, avgScore: 4.5, color: '#10b981' },
      { name: 'Bathroom Renovation', responses: 28, avgScore: 4.3, color: '#3b82f6' },
      { name: 'Outdoor Construction', responses: 22, avgScore: 4.6, color: '#f59e0b' },
      { name: 'Flooring', responses: 18, avgScore: 4.2, color: '#ef4444' },
      { name: 'Other', responses: 12, avgScore: 4.1, color: '#8b5cf6' }
    ],
    qualityMetrics: {
      completionRate: { current: 92.8, previous: 88.5, unit: '%' },
      avgResponseTime: { current: 12.3, previous: 15.8, unit: 'min' },
      avgQualityScore: { current: 4.4, previous: 4.1, unit: '/5' },
      vendorEngagement: { current: 87, previous: 82, unit: '%' }
    }
  };

  // Mock vendor comparison data
  const vendorComparisons = [
    {
      id: 1,
      name: 'Premier Home Builders',
      category: 'Kitchen Remodeling',
      responsesSubmitted: 24,
      completionRate: 95.8,
      avgResponseTime: 10.5,
      qualityScore: 4.7,
      reliabilityRating: 'Excellent',
      strengths: ['Detailed responses', 'Fast turnaround', 'Comprehensive pricing'],
      weaknesses: ['Limited availability'],
      lastResponse: '2024-11-15'
    },
    {
      id: 2,
      name: 'Elite Renovations',
      category: 'Bathroom Renovation',
      responsesSubmitted: 19,
      completionRate: 89.5,
      avgResponseTime: 14.2,
      qualityScore: 4.3,
      reliabilityRating: 'Good',
      strengths: ['Competitive pricing', 'Good communication'],
      weaknesses: ['Slower response time', 'Incomplete specifications'],
      lastResponse: '2024-11-14'
    },
    {
      id: 3,
      name: 'Outdoor Living Experts',
      category: 'Outdoor Construction',
      responsesSubmitted: 22,
      completionRate: 95.5,
      avgResponseTime: 11.8,
      qualityScore: 4.6,
      reliabilityRating: 'Excellent',
      strengths: ['Expert knowledge', 'Detailed proposals', 'High quality'],
      weaknesses: ['Premium pricing'],
      lastResponse: '2024-11-16'
    },
    {
      id: 4,
      name: 'FloorMasters Pro',
      category: 'Flooring',
      responsesSubmitted: 18,
      completionRate: 88.9,
      avgResponseTime: 15.5,
      qualityScore: 4.2,
      reliabilityRating: 'Good',
      strengths: ['Wide material selection', 'Flexible scheduling'],
      weaknesses: ['Variable response quality', 'Longer response time'],
      lastResponse: '2024-11-13'
    },
    {
      id: 5,
      name: 'Complete Home Solutions',
      category: 'Kitchen Remodeling',
      responsesSubmitted: 16,
      completionRate: 93.8,
      avgResponseTime: 12.0,
      qualityScore: 4.5,
      reliabilityRating: 'Excellent',
      strengths: ['Comprehensive service', 'Good value', 'Reliable'],
      weaknesses: ['Limited design options'],
      lastResponse: '2024-11-15'
    }
  ];

  // Mock project data with questionnaire responses
  const mockProjects = [
    {
      id: 1,
      name: 'Kitchen Remodeling - Downtown Condo',
      category: 'Kitchen Remodeling',
      createdDate: '2024-11-01',
      questionnaire: [
        { id: 'experience', name: 'Years of Experience', description: 'How many years have you been in business?', priority: 'High', icon: 'Award' },
        { id: 'portfolio', name: 'Portfolio Quality', description: 'Quality of past work examples', priority: 'High', icon: 'Image' },
        { id: 'pricing', name: 'Competitive Pricing', description: 'Price competitiveness', priority: 'High', icon: 'DollarSign' },
        { id: 'timeline', name: 'Project Timeline', description: 'Estimated completion time', priority: 'Medium', icon: 'Clock' },
        { id: 'warranty', name: 'Warranty Coverage', description: 'Warranty terms offered', priority: 'Medium', icon: 'Shield' },
        { id: 'communication', name: 'Communication', description: 'Responsiveness and clarity', priority: 'Low', icon: 'MessageSquare' }
      ]
    },
    {
      id: 2,
      name: 'Bathroom Renovation - Suburban Home',
      category: 'Bathroom Renovation',
      createdDate: '2024-10-28',
      questionnaire: [
        { id: 'experience', name: 'Years of Experience', description: 'How many years have you been in business?', priority: 'High', icon: 'Award' },
        { id: 'licensing', name: 'Licensing & Insurance', description: 'Valid licenses and insurance coverage', priority: 'High', icon: 'FileCheck' },
        { id: 'pricing', name: 'Competitive Pricing', description: 'Price competitiveness', priority: 'High', icon: 'DollarSign' },
        { id: 'materials', name: 'Material Quality', description: 'Quality of materials used', priority: 'Medium', icon: 'Package' },
        { id: 'timeline', name: 'Project Timeline', description: 'Estimated completion time', priority: 'Medium', icon: 'Clock' },
        { id: 'references', name: 'References', description: 'Customer references available', priority: 'Low', icon: 'Users' }
      ]
    }
  ];

  // Mock vendor responses to questionnaires
  const vendorResponses = [
    {
      projectId: 1,
      vendorId: 1,
      vendorName: 'Premier Home Builders',
      submittedDate: '2024-11-05',
      responses: {
        experience: { answer: '15 years', score: 95, status: 'excellent', note: '15 years in business' },
        portfolio: { answer: 'Extensive portfolio with 50+ projects', score: 98, status: 'excellent', note: 'High-quality work samples' },
        pricing: { answer: '$45,000', score: 88, status: 'good', note: 'Competitive pricing' },
        timeline: { answer: '6-8 weeks', score: 92, status: 'excellent', note: 'Fast turnaround' },
        warranty: { answer: '5-year comprehensive warranty', score: 95, status: 'excellent', note: 'Excellent coverage' },
        communication: { answer: 'Available 24/7, responds within 2 hours', score: 100, status: 'excellent', note: 'Highly responsive' }
      }
    },
    {
      projectId: 1,
      vendorId: 2,
      vendorName: 'Elite Renovations',
      submittedDate: '2024-11-06',
      responses: {
        experience: { answer: '8 years', score: 80, status: 'good', note: '8 years in business' },
        portfolio: { answer: 'Good portfolio with 30+ projects', score: 85, status: 'good', note: 'Quality work samples' },
        pricing: { answer: '$38,000', score: 95, status: 'excellent', note: 'Best pricing' },
        timeline: { answer: '8-10 weeks', score: 85, status: 'good', note: 'Standard timeline' },
        warranty: { answer: '3-year warranty', score: 80, status: 'good', note: 'Good coverage' },
        communication: { answer: 'Available weekdays, responds within 24 hours', score: 75, status: 'fair', note: 'Moderate responsiveness' }
      }
    },
    {
      projectId: 1,
      vendorId: 5,
      vendorName: 'Complete Home Solutions',
      submittedDate: '2024-11-07',
      responses: {
        experience: { answer: '12 years', score: 90, status: 'excellent', note: '12 years in business' },
        portfolio: { answer: 'Strong portfolio with 40+ projects', score: 90, status: 'excellent', note: 'Quality work samples' },
        pricing: { answer: '$42,000', score: 90, status: 'excellent', note: 'Fair pricing' },
        timeline: { answer: '7-9 weeks', score: 88, status: 'good', note: 'Good timeline' },
        warranty: { answer: '4-year warranty', score: 88, status: 'good', note: 'Good coverage' },
        communication: { answer: 'Available daily, responds within 4 hours', score: 90, status: 'excellent', note: 'Good responsiveness' }
      }
    }
  ];

  // Mock sentiment analysis data
  const sentimentData = [
    { sentiment: 'Very Positive', count: 45, percentage: 39, color: '#10b981' },
    { sentiment: 'Positive', count: 38, percentage: 33, color: '#3b82f6' },
    { sentiment: 'Neutral', count: 22, percentage: 19, color: '#f59e0b' },
    { sentiment: 'Negative', count: 8, percentage: 7, color: '#ef4444' },
    { sentiment: 'Very Negative', count: 2, percentage: 2, color: '#dc2626' }
  ];

  // Mock keyword frequency data
  const keywordData = [
    { keyword: 'Quality', frequency: 89 },
    { keyword: 'Experience', frequency: 76 },
    { keyword: 'Professional', frequency: 68 },
    { keyword: 'Reliable', frequency: 62 },
    { keyword: 'Competitive', frequency: 54 },
    { keyword: 'Fast', frequency: 48 },
    { keyword: 'Detailed', frequency: 42 },
    { keyword: 'Comprehensive', frequency: 38 }
  ];

  // Filter vendors based on selected vendor
  const filteredVendors = vendorComparisons?.filter(vendor => {
    if (selectedVendor !== 'all' && vendor?.name !== selectedVendor) return false;
    return true;
  });

  const getReliabilityColor = (rating) => {
    switch (rating) {
      case 'Excellent': return 'bg-green-100 text-green-800';
      case 'Good': return 'bg-blue-100 text-blue-800';
      case 'Fair': return 'bg-yellow-100 text-yellow-800';
      case 'Poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExportReport = () => {
    alert('Exporting analytics report... (PDF/Excel format)');
  };

  // Filter vendor responses based on selected project
  const filteredResponses = vendorResponses?.filter(response => {
    if (selectedProject !== 'all' && response?.projectId !== parseInt(selectedProject)) return false;
    return true;
  });

  // Get questionnaire for selected project
  const getProjectQuestionnaire = () => {
    if (selectedProject === 'all') return [];
    const project = mockProjects?.find(p => p?.id === parseInt(selectedProject));
    return project?.questionnaire || [];
  };

  // Calculate overall score for vendor response
  const calculateOverallScore = (responses, questionnaire) => {
    if (!compareWeights || !questionnaire?.length) {
      // Default priority-based calculation
      const scores = questionnaire?.map(q => {
        const response = responses?.[q?.id];
        const weight = q?.priority === 'High' ? 3 : q?.priority === 'Medium' ? 2 : 1;
        return (response?.score || 0) * weight;
      });
      const totalWeight = questionnaire?.reduce((sum, q) => 
        sum + (q?.priority === 'High' ? 3 : q?.priority === 'Medium' ? 2 : 1), 0
      );
      return Math.round(scores?.reduce((sum, score) => sum + score, 0) / totalWeight);
    }
    
    // Custom weights calculation
    const scores = questionnaire?.map(q => {
      const response = responses?.[q?.id];
      const weight = compareWeights?.[q?.id] || 0;
      return (response?.score || 0) * (weight / 100);
    });
    return Math.round(scores?.reduce((sum, score) => sum + score, 0));
  };

  // Get best vendor recommendation
  const getBestVendorRecommendation = () => {
    if (!filteredResponses?.length) return null;
    const questionnaire = getProjectQuestionnaire();
    if (!questionnaire?.length) return null;
    
    return filteredResponses?.reduce((best, current) => {
      const currentScore = calculateOverallScore(current?.responses, questionnaire);
      const bestScore = best ? calculateOverallScore(best?.responses, questionnaire) : 0;
      return currentScore > bestScore ? current : best;
    }, null);
  };

  // Handle weighting
  const handleWeightChange = (questionId, value) => {
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setCompareWeights(prev => ({ ...prev, [questionId]: value }));
    }
  };

  const calculateWeightTotal = () => {
    if (!compareWeights) return 0;
    return Object.values(compareWeights)?.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleApplyWeights = () => {
    const total = calculateWeightTotal();
    const questionnaire = getProjectQuestionnaire();
    const allFilled = questionnaire?.every(q => compareWeights?.[q?.id] && compareWeights?.[q?.id] !== '');
    
    if (Math.abs(total - 100) > 0.01 || !allFilled) {
      alert('Please ensure all weights are filled and total equals 100%');
      return;
    }
    
    // Convert string weights to numbers
    const numericWeights = {};
    Object.keys(compareWeights)?.forEach(key => {
      numericWeights[key] = parseFloat(compareWeights?.[key]);
    });
    
    setCompareWeights(numericWeights);
    setShowCompareCalculations(true);
    setIsWeightingExpanded(false);
  };

  const handleDistributeEvenly = () => {
    const questionnaire = getProjectQuestionnaire();
    if (!questionnaire?.length) return;
    
    const evenWeight = (100 / questionnaire?.length)?.toFixed(2);
    const newWeights = {};
    questionnaire?.forEach((q, index) => {
      if (index === questionnaire?.length - 1) {
        const currentTotal = (questionnaire?.length - 1) * parseFloat(evenWeight);
        newWeights[q?.id] = (100 - currentTotal)?.toFixed(2);
      } else {
        newWeights[q?.id] = evenWeight;
      }
    });
    setCompareWeights(newWeights);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'fair':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'poor':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const bestVendorResponse = getBestVendorRecommendation();
  const questionnaire = getProjectQuestionnaire();
  const weightTotal = calculateWeightTotal();
  const isValidTotal = Math.abs(weightTotal - 100) < 0.01;
  const allWeightsFilled = questionnaire?.every(q => compareWeights?.[q?.id] && compareWeights?.[q?.id] !== '');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/all-projects' },
    { label: 'Vendor Analytics', path: '/vendor-analytics-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-32">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            
            {/* Header */}
            <div className="mb-8 mt-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Analytics Dashboard</h1>
              <p className="text-gray-600">Comprehensive insights from vendor questionnaire responses and performance metrics</p>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1 mb-8">
              <div className="flex flex-wrap">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'overview' ? 'bg-green-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('comparison')}
                  className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'comparison' ? 'bg-green-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Vendor Comparison
                </button>
                <button
                  onClick={() => setActiveTab('compare')}
                  className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'compare' ? 'bg-green-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Compare
                </button>
                <button
                  onClick={() => setActiveTab('insights')}
                  className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'insights' ? 'bg-green-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Advanced Insights
                </button>
              </div>
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Date Range & Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Date Range:</span>
                      <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e?.target?.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="6m">Last 6 months</option>
                        <option value="1y">Last year</option>
                      </select>
                      <span className="text-sm font-medium text-gray-700 ml-4">Category:</span>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e?.target?.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="all">All Categories</option>
                        <option value="Kitchen Remodeling">Kitchen Remodeling</option>
                        <option value="Bathroom Renovation">Bathroom Renovation</option>
                        <option value="Outdoor Construction">Outdoor Construction</option>
                        <option value="Flooring">Flooring</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        iconName="Filter"
                        className="border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        Filter
                      </Button>
                      <Button
                        onClick={handleExportReport}
                        iconName="Download"
                        className="bg-green-600 text-white hover:bg-green-700"
                      >
                        Export Report
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Key Performance Indicators */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">
                        +{((responseData?.qualityMetrics?.completionRate?.current - responseData?.qualityMetrics?.completionRate?.previous) / responseData?.qualityMetrics?.completionRate?.previous * 100)?.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Response Completion Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{responseData?.qualityMetrics?.completionRate?.current}%</p>
                    <p className="text-xs text-gray-500 mt-2">Previous: {responseData?.qualityMetrics?.completionRate?.previous}%</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">
                        -{((responseData?.qualityMetrics?.avgResponseTime?.previous - responseData?.qualityMetrics?.avgResponseTime?.current) / responseData?.qualityMetrics?.avgResponseTime?.previous * 100)?.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
                    <p className="text-2xl font-bold text-gray-900">{responseData?.qualityMetrics?.avgResponseTime?.current} min</p>
                    <p className="text-xs text-gray-500 mt-2">Previous: {responseData?.qualityMetrics?.avgResponseTime?.previous} min</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Star className="w-5 h-5 text-yellow-600" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">
                        +{((responseData?.qualityMetrics?.avgQualityScore?.current - responseData?.qualityMetrics?.avgQualityScore?.previous) / responseData?.qualityMetrics?.avgQualityScore?.previous * 100)?.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Avg Quality Score</p>
                    <p className="text-2xl font-bold text-gray-900">{responseData?.qualityMetrics?.avgQualityScore?.current}/5</p>
                    <p className="text-xs text-gray-500 mt-2">Previous: {responseData?.qualityMetrics?.avgQualityScore?.previous}/5</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="text-xs text-green-600 font-medium">
                        +{((responseData?.qualityMetrics?.vendorEngagement?.current - responseData?.qualityMetrics?.vendorEngagement?.previous) / responseData?.qualityMetrics?.vendorEngagement?.previous * 100)?.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Vendor Engagement</p>
                    <p className="text-2xl font-bold text-gray-900">{responseData?.qualityMetrics?.vendorEngagement?.current}%</p>
                    <p className="text-xs text-gray-500 mt-2">Previous: {responseData?.qualityMetrics?.vendorEngagement?.previous}%</p>
                  </div>
                </div>

                {/* Response Trends Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Response Submission Trends</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Submitted</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Completed</span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={responseData?.responseRates}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Bar dataKey="submitted" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="completed" fill="#10b981" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Category Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Response Distribution by Category</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={responseData?.categoryDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="responses"
                        >
                          {responseData?.categoryDistribution?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry?.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Average Response Time Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={responseData?.responseRates}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="avgTime" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'comparison' && (
              <div className="space-y-8">
                {/* Vendor Filter */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Filter by Vendor:</span>
                    <select
                      value={selectedVendor}
                      onChange={(e) => setSelectedVendor(e?.target?.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">All Vendors</option>
                      {vendorComparisons?.map(vendor => (
                        <option key={vendor?.id} value={vendor?.name}>{vendor?.name}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500 ml-auto">Showing {filteredVendors?.length} of {vendorComparisons?.length} vendors</span>
                  </div>
                </div>

                {/* Vendor Comparison Cards */}
                <div className="space-y-6">
                  {filteredVendors?.map(vendor => (
                    <div key={vendor?.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        {/* Vendor Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900 mb-1">{vendor?.name}</h3>
                              <p className="text-sm text-gray-600">{vendor?.category}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReliabilityColor(vendor?.reliabilityRating)}`}>
                              {vendor?.reliabilityRating}
                            </span>
                          </div>

                          {/* Metrics Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Responses</p>
                              <p className="text-lg font-semibold text-gray-900">{vendor?.responsesSubmitted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Completion Rate</p>
                              <p className="text-lg font-semibold text-gray-900">{vendor?.completionRate}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Avg Time</p>
                              <p className="text-lg font-semibold text-gray-900">{vendor?.avgResponseTime} min</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Quality Score</p>
                              <p className="text-lg font-semibold text-gray-900">{vendor?.qualityScore}/5</p>
                            </div>
                          </div>

                          {/* Strengths & Weaknesses */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <ThumbsUp className="w-4 h-4 text-green-600" />
                                <p className="text-sm font-medium text-gray-700">Strengths</p>
                              </div>
                              <ul className="space-y-1">
                                {vendor?.strengths?.map((strength, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                    <span className="text-green-600 mt-1">•</span>
                                    <span>{strength}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <AlertCircle className="w-4 h-4 text-orange-600" />
                                <p className="text-sm font-medium text-gray-700">Areas for Improvement</p>
                              </div>
                              <ul className="space-y-1">
                                {vendor?.weaknesses?.map((weakness, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                    <span className="text-orange-600 mt-1">•</span>
                                    <span>{weakness}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <p className="text-xs text-gray-500 mt-4">Last response: {vendor?.lastResponse}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'compare' && (
              <div className="space-y-8">
                {/* Project Selection */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Select Project:</span>
                    <select
                      value={selectedProject}
                      onChange={(e) => {
                        setSelectedProject(e?.target?.value);
                        setCompareWeights(null);
                        setShowCompareCalculations(false);
                        setIsWeightingExpanded(true);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">Select a project to compare vendor responses</option>
                      {mockProjects?.map(project => (
                        <option key={project?.id} value={project?.id}>{project?.name}</option>
                      ))}
                    </select>
                    {selectedProject !== 'all' && (
                      <span className="text-sm text-gray-500 ml-auto">Showing {filteredResponses?.length} vendor responses</span>
                    )}
                  </div>
                </div>

                {selectedProject !== 'all' && questionnaire?.length > 0 && (
                  <>
                    {/* Weighting Section */}
                    <div className="bg-white rounded-lg border-2 border-blue-500 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b-2 border-blue-400">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Sliders className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">Adjust Importance Weighting</h3>
                              <p className="text-sm text-gray-600">
                                Assign percentage weights to each question (must total 100%)
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            {!isWeightingExpanded && (
                              <div className="flex items-center space-x-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                <span className="text-sm font-medium text-green-700">Weights Applied</span>
                              </div>
                            )}
                            <Button
                              onClick={() => setIsWeightingExpanded(!isWeightingExpanded)}
                              variant="outline"
                              size="sm"
                              iconName={isWeightingExpanded ? "ChevronUp" : "ChevronDown"}
                              iconPosition="right"
                            >
                              {isWeightingExpanded ? 'Collapse' : 'Expand'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {isWeightingExpanded && (
                        <div className="p-6">
                          <div className="mb-4 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              <AlertCircle className="inline w-4 h-4 mr-1" />
                              Assign higher percentages to more important questions
                            </div>
                            <Button
                              onClick={handleDistributeEvenly}
                              variant="outline"
                              size="sm"
                              iconName="Percent"
                              iconPosition="left"
                            >
                              Distribute Evenly
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {questionnaire?.map((question) => (
                              <div key={question?.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                                <div className="flex items-start space-x-3">
                                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                                    question?.priority === 'High' ? 'bg-red-100 text-red-600' :
                                    question?.priority === 'Medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'
                                  }`}>
                                    <Award className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 mb-1">{question?.name}</h4>
                                    <p className="text-xs text-gray-600 mb-3">{question?.description}</p>
                                    <div className="flex items-center space-x-2">
                                      <div className="relative flex-1">
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="0.1"
                                          value={compareWeights?.[question?.id] || ''}
                                          onChange={(e) => handleWeightChange(question?.id, e?.target?.value)}
                                          placeholder="0.00"
                                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-right pr-8"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
                                      </div>
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        question?.priority === 'High' ? 'bg-red-100 text-red-800' :
                                        question?.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                      }`}>
                                        {question?.priority}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="border-t-2 border-gray-200 pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium">Total Weight:</span>
                                </div>
                                <div className={`text-2xl font-bold ${
                                  isValidTotal ? 'text-green-600' : weightTotal > 100 ? 'text-red-600' : 'text-yellow-600'
                                }`}>
                                  {weightTotal?.toFixed(2)}%
                                </div>
                                {isValidTotal && allWeightsFilled && (
                                  <div className="flex items-center space-x-1 text-green-600">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span className="text-sm font-medium">Ready</span>
                                  </div>
                                )}
                              </div>
                              <Button
                                onClick={handleApplyWeights}
                                disabled={!isValidTotal || !allWeightsFilled}
                                iconName="Check"
                                iconPosition="left"
                                className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                Apply Weights
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Comparison Table */}
                    <div className="bg-white rounded-lg border-2 border-green-500 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-50 to-green-50 px-6 py-4 border-b-2 border-green-400">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">Vendor Response Comparison</h3>
                            <p className="text-sm text-gray-600">
                              {showCompareCalculations 
                                ? 'Compare how each vendor responded to your questionnaire with weighted scoring'
                                : 'Complete weighting above to see calculated recommendations'
                              }
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            {bestVendorResponse && showCompareCalculations && (
                              <div className="text-right">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Trophy className="w-5 h-5 text-yellow-500" />
                                  <span className="text-sm font-medium text-gray-700">Best Match:</span>
                                  <span className="text-lg font-bold text-blue-600">{bestVendorResponse?.vendorName}</span>
                                </div>
                              </div>
                            )}
                            <Button
                              onClick={() => setIsComparisonExpanded(!isComparisonExpanded)}
                              variant="outline"
                              size="sm"
                              iconName={isComparisonExpanded ? "ChevronUp" : "ChevronDown"}
                              iconPosition="right"
                            >
                              {isComparisonExpanded ? 'Collapse' : 'Expand'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {isComparisonExpanded && (
                        <div className="overflow-x-auto">
                          <table className="w-full border-2 border-green-400">
                            <thead>
                              <tr className="border-b-2 border-green-400">
                                <th className="sticky left-0 bg-white px-6 py-4 text-left border-r-2 border-green-400 w-64">
                                  <div className="text-sm font-medium text-gray-900">Questions</div>
                                </th>
                                {filteredResponses?.map((response, index) => {
                                  const isTopVendor = bestVendorResponse?.vendorId === response?.vendorId && showCompareCalculations;
                                  return (
                                    <th key={index} className={`px-4 py-4 text-center min-w-48 ${
                                      isTopVendor ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'
                                    } border-r-2 border-green-400`}>
                                      <div className="space-y-3">
                                        <div className={`relative ${isTopVendor ? 'ring-2 ring-blue-400 rounded-lg p-2' : ''}`}>
                                          {isTopVendor && (
                                            <div className="absolute -top-2 -right-2 bg-yellow-500 text-white rounded-full p-1">
                                              <Crown className="w-4 h-4" />
                                            </div>
                                          )}
                                          <h4 className="font-medium text-gray-900">{response?.vendorName}</h4>
                                          <p className="text-xs text-gray-500">Submitted: {response?.submittedDate}</p>
                                        </div>
                                        {showCompareCalculations && (
                                          <div className="bg-white rounded-lg p-2 border border-gray-200">
                                            <div className="text-xs text-gray-600 mb-1">Overall Score</div>
                                            <div className="text-2xl font-bold text-green-600">
                                              {calculateOverallScore(response?.responses, questionnaire)}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {questionnaire?.map((question, qIndex) => (
                                <tr key={question?.id} className={qIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                  <td className="sticky left-0 bg-white px-6 py-4 border-r-2 border-green-400">
                                    <div className="flex items-start space-x-3">
                                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                                        question?.priority === 'High' ? 'bg-red-100 text-red-600' :
                                        question?.priority === 'Medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'
                                      }`}>
                                        <Award className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <h4 className="font-medium text-gray-900 mb-1">{question?.name}</h4>
                                        <p className="text-xs text-gray-600">{question?.description}</p>
                                        {showCompareCalculations && compareWeights?.[question?.id] && (
                                          <div className="mt-2 inline-flex items-center px-2 py-1 bg-blue-50 rounded text-xs font-medium text-blue-700">
                                            Weight: {compareWeights?.[question?.id]}%
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  {filteredResponses?.map((response, rIndex) => {
                                    const answer = response?.responses?.[question?.id];
                                    return (
                                      <td key={rIndex} className="px-4 py-4 text-center border-r-2 border-green-400">
                                        {answer ? (
                                          <div className="space-y-2">
                                            <div className="text-sm text-gray-900 font-medium">{answer?.answer}</div>
                                            {showCompareCalculations && (
                                              <>
                                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(answer?.status)}`}>
                                                  {answer?.status?.toUpperCase()}
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">Score: {answer?.score}</div>
                                                <div className="text-xs text-gray-600">{answer?.note}</div>
                                              </>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-sm text-gray-400">No response</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {selectedProject === 'all' && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Project to Compare</h3>
                    <p className="text-gray-600">
                      Choose a project from the dropdown above to compare vendor questionnaire responses
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="space-y-8">
                {/* Sentiment Analysis */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <MessageSquare className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Sentiment Analysis of Open-Ended Responses</h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={sentimentData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ sentiment, percentage }) => `${sentiment}: ${percentage}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {sentimentData?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry?.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-3">
                      {sentimentData?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item?.color }}></div>
                            <span className="text-sm font-medium text-gray-700">{item?.sentiment}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{item?.count} responses</p>
                            <p className="text-xs text-gray-500">{item?.percentage}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Keyword Frequency */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <FileText className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Keyword Frequency Mapping</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={keywordData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" stroke="#6b7280" />
                      <YAxis dataKey="keyword" type="category" stroke="#6b7280" width={120} />
                      <Tooltip />
                      <Bar dataKey="frequency" fill="#10b981" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Predictive Scoring */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Award className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Predictive Vendor Selection Recommendations</h3>
                  </div>
                  <div className="space-y-4">
                    {vendorComparisons?.slice(0, 3)?.map((vendor, idx) => (
                      <div key={vendor?.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-full font-bold">
                            #{idx + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{vendor?.name}</p>
                            <p className="text-sm text-gray-600">{vendor?.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Recommendation Score</p>
                          <p className="text-2xl font-bold text-green-600">{(vendor?.qualityScore * 20)?.toFixed(0)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Based on response quality, completion rate, and historical performance data</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VendorAnalyticsDashboard;